import type { CodexSectionView } from "./senses"

export interface ExampleToken {
  /** The dynamic string, e.g. "{{ genitive_singular }}". */
  token: string
  /** What it resolves to for this entry, e.g. "reatha" — shown as the example. */
  value: string
  /** Human label, e.g. "Genitive singular" or "Past · he". */
  label: string
}

export interface ExampleTokenContext {
  /** Resolution context for the live preview (key -> resolved form). */
  context: Record<string, string>
  /** The supported dynamic strings for this entry, with a worked example each. */
  tokens: ExampleToken[]
}

/**
 * Derives what an example can reference for a given headword: the base word, its grammar fields, and
 * its conjugation cells, each as a `{{ token }}` with the value it resolves to. Merged across all the
 * entry's sections so the editor lists everything available. Powers both the token help and the live
 * preview context (they stay in sync with what the page actually renders).
 */
export function buildExampleTokens(
  sections: CodexSectionView[] | undefined,
  headword: string
): ExampleTokenContext {
  const context: Record<string, string> = { name: headword, headword }
  const tokens: ExampleToken[] = [{ token: "{{ name }}", value: headword, label: "Headword" }]

  sections?.forEach((section) => {
    section.grammar.forEach((row) => {
      if (row.key && row.value) {
        context[row.key] = row.value
        tokens.push({ token: `{{ ${row.key} }}`, value: row.value, label: row.label })
      }
    })
    section.conjugation?.tenses.forEach((tense) => {
      // Tense base: the analytic verb form used before a subject noun, e.g. {{ past }} -> "rith" for
      // "Rith Grug go tapa" (Grug ran fast). Derived from the 3rd-person form (verb + pronoun) by
      // dropping the pronoun; falls back to the first form.
      const anchor = tense.forms.find((form) => form.person === "he") ?? tense.forms[0]
      if (anchor) {
        const base = anchor.form.split(" ")[0]
        context[tense.tense] = base
        tokens.push({ token: `{{ ${tense.tense} }}`, value: base, label: `${tense.label} (verb)` })
      }

      tense.forms.forEach((form) => {
        const key = `${tense.tense}.${form.person}`
        context[key] = form.form
        tokens.push({ token: `{{ ${key} }}`, value: form.form, label: `${tense.label} · ${form.label}` })
      })
    })
  })

  // Dedupe by token (a grammar field can repeat across sections).
  const seen = new Set<string>()
  const uniqueTokens = tokens.filter((t) => (seen.has(t.token) ? false : (seen.add(t.token), true)))

  return { context, tokens: uniqueTokens }
}

/** The casing filters an example can pipe a token through, e.g. `{{ name | capitalize }}`. */
export const EXAMPLE_FILTERS = ["capitalize", "downcase", "upcase"] as const
